/**
 * Resolve the thumbnail URL to render in a Studio AI gallery <img>.
 *
 * Returns a cached blob: URL (fed via the batch endpoint), the legacy
 * fallback URL the caller already had (e.g. a data: URL the generator
 * just produced and stashed in ImageCacheService), or `null` while a
 * fetch is in flight.
 *
 * Why not just use the legacy URL directly? It's usually
 * `/api/studio-ai/images/<job_id>`, which requires auth that <img src>
 * tags can't send. The hook prefers the cached blob URL when present,
 * because that one always renders.
 *
 * Uncached lookups for ids we haven't seen yet join the shared 50ms
 * debounce window in `use-image-thumbnail-lazy-request`, so a gallery
 * mount with N cards fires ONE batch HTTP call with N ids.
 *
 * `onImgError` is exposed so the caller can surface the broken-image
 * state when even the final URL fails to render in the browser.
 */

import { useEffect, useState } from "react"

import { requestImageThumbnail } from "./use-image-thumbnail-lazy-request"
import { useImageThumbnailCache } from "./use-image-thumbnail-prefetch"

/** Is the URL one the <img> tag can actually load without sending auth headers? */
function isImgSafe(url: string | null | undefined): url is string {
  if (!url) return false
  return url.startsWith("data:") || url.startsWith("blob:")
}

export function useStudioImageThumbnail(
  jobId: string | undefined,
  legacyUrl: string | null | undefined,
  options?: { autoRequest?: boolean }
) {
  const autoRequest = options?.autoRequest ?? true
  const cachedUrl = useImageThumbnailCache((s) => (jobId ? s.urls.get(jobId) : undefined))
  const isPending = useImageThumbnailCache((s) => (jobId ? s.inFlight.has(jobId) : false))
  const hasFailed = useImageThumbnailCache((s) => (jobId ? s.failed.has(jobId) : false))
  const [imgError, setImgError] = useState(false)

  // Reset img-level error state when the underlying id changes (e.g. card
  // recycled by a virtualized list).
  useEffect(() => {
    setImgError(false)
  }, [jobId, cachedUrl])

  useEffect(() => {
    if (!jobId) return
    if (!autoRequest) return
    // A falsy legacyUrl means the job has no persisted image URL yet — i.e.
    // generation is still in flight (currentJobId is set the moment streaming
    // starts, but the /api URL only exists once the blob is written). Firing
    // the batch now hits the endpoint before any blob exists, which reports
    // the id in `missing` and permanently poisons the cache for the session,
    // so the thumbnail never loads even after the job completes (only a hard
    // reload clears it). Wait until we have an /api URL to upgrade into a
    // renderable blob URL. Callers that fetch by id alone (gallery cards) go
    // through the autoRequest:false + IntersectionObserver path instead.
    // (Checked before isImgSafe so its `url is string` predicate doesn't
    // narrow legacyUrl and make this read as always-falsy.)
    if (!legacyUrl) return
    // We already have something the <img> can render — no batch needed.
    if (cachedUrl || isImgSafe(legacyUrl)) return
    if (isPending || hasFailed) return
    // Coalesce — multiple simultaneous mounts share one HTTP batch.
    requestImageThumbnail(jobId)
  }, [jobId, autoRequest, cachedUrl, legacyUrl, isPending, hasFailed])

  // Resolution priority:
  //   1. cached blob URL (always renders, no auth)
  //   2. legacy URL only if it's already img-safe (data: / blob:)
  //   3. null while pending
  const url = cachedUrl ?? (isImgSafe(legacyUrl) ? legacyUrl : null)
  // Only surface a failure when we actually have nothing to render — having
  // a URL means consumers should display it regardless of any stale flag.
  // setUrls() also clears `failed` on success so this is belt-and-braces,
  // but it keeps the contract crisp at the read site too.
  const error = (!url && hasFailed) || imgError
  // Distinguish "fetch in flight" from "failed". media-preview-card otherwise
  // can't tell the two apart and shows 'image not available' as soon as the
  // batch fires, before the blob bytes arrive.
  const loading = !url && !error

  return {
    url,
    error,
    loading,
    onImgError: () => setImgError(true),
  }
}
